<script setup lang="ts">
interface Industries {
  name: string;
}

const route = useRoute();
const { defineOgMeta } = useSeoAttributes();
const localePath = useLocalePath();
const {
  public: { baseUrl },
} = useRuntimeConfig();

const projectFetch = useProjects();

const { pending, data: item } = await useAsyncData(
  `projectPage-${route.params.slug}`,
  async () => {
    return projectFetch.fetchBySlug(
      route.params.slug as string,
      route.params.industry as string
    );
  },
  { watch: [route] }
);

if (!item.value?.project) {
  throw showError({
    statusCode: 404,
  });
}

if (item.value?.project) {
  useHead(() => ({
    title: item.value!.project.meta_title ?? item.value!.project.title,
    meta: [...defineOgMeta(item.value!.meta)],
    link: [
      {
        rel: "canonical",
        href: `${baseUrl}/projects/${route.params.industry}/${route.params.slug}`,
      },
    ],
  }));
}

const breadcrumbs = [
  { href: "/projects", label: "Our Work" },
  {
    href: `/projects/${route.params.industry}`,
    label:
      item.value?.project?.industries?.find(
        (industry) => industry.slug === route.params.industry
      )?.name || "",
  },
  {
    href: `/projects/${route.params.industry}/${route.params.slug}`,
    label: item.value?.project?.title || "",
  },
];
</script>

<template>
  <Loader :pending="pending">
    <div>
      <Container v-if="item?.project" class="screen-content px-0 pb-8">
        <Breadcrumbs :items="breadcrumbs" class="pl-8 pt-20 md:pl-16" />

        <LayoutPageHeader
          :title="item.project.title"
          :country="item.project.country_name"
        >
          <template #eyebrow>
            {{
              item.project.industries
                .map((i: Industries) => i.name)
                ?.join(", ") +
              (item.project.country_name
                ? " — " + item.project.country_name
                : "")
            }}
          </template>

          <template #description>
            {{ item.project.description }}
          </template>
        </LayoutPageHeader>

        <div class="px-2">
          <div
            class="bordershadow-medium mx-auto mb-8 max-w-screen-lg rounded-lg bg-white p-2"
          >
            <NuxtImg
              fit="cover"
              class="bordershadow-gray mx-auto aspect-video w-full rounded object-cover"
              :src="item.project.image.url"
              :alt="
                item.project.image.alternativeText || item.project.image.name
              "
              :width="item.project.image.width"
              :height="item.project.image.height"
            />
          </div>
        </div>

        <div class="flex w-full flex-col gap-y-16 px-8 md:px-16 lg:px-0">
          <ProjectSpecialSection
            id="overview"
            :title="item.project.overviewTitle || $t('projects.overview')"
          >
            <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 md:w-2/3">
              <ProjectOverviewItem
                :title="$t('projects.solution')"
                :body="item.project.solution"
              />
              <ProjectOverviewItem
                :title="$t('projects.duration')"
                :body="item.project.duration"
              />
              <ProjectOverviewItem
                :title="$t('projects.team')"
                :body="item.project.team"
              />
              <ProjectOverviewItem
                :title="$t('projects.pricingModel')"
                :body="item.project.pricingModel"
              />
            </div>
          </ProjectSpecialSection>

          <RichText
            v-if="item.project.overview"
            :raw="item.project.overview"
            class="mx-auto"
          />

          <ProjectSpecialSection
            v-if="item.project.technologies?.length"
            id="technologies"
            :title="$t('projects.built_with')"
          >
            <div
              class="mt-4 grid grid-cols-2 flex-row flex-wrap items-center justify-center gap-x-7 gap-y-6 lg:flex lg:gap-4 lg:px-8"
            >
              <div
                v-for="tech of item.project.technologies"
                :key="`tech-${tech.id}`"
                class="h-28 w-28"
              >
                <ProjectTechItem :tech="tech" />
              </div>
            </div>
          </ProjectSpecialSection>

          <ProjectSpecialSection
            v-if="item.project.highlights?.length"
            id="highlights"
            :title="$t('projects.key_highlights')"
          >
            <div
              class="mx-auto grid w-full grid-cols-1 gap-4 sm:grid-cols-2 md:w-2/3"
            >
              <div
                v-for="highlight of item.project.highlights"
                :key="highlight.highlight"
                class="w-full rounded-lg bg-gray-100 p-4"
              >
                <p class="text-lg font-bold">
                  {{ highlight.highlight }}
                </p>
              </div>
            </div>
          </ProjectSpecialSection>

          <section class="w-full">
            <RichText :raw="item.project.content" class="mx-auto" />
          </section>

          <ProjectSpecialSection
            v-if="item.project.features?.length"
            id="features"
            :title="$t('projects.features')"
          >
            <div class="mx-auto flex w-full flex-wrap justify-center md:w-2/3">
              <ProjectFeature
                v-for="feature in item.project.features"
                :key="`feature-${feature.id}`"
                class="mb-8 w-1/2 px-4 lg:w-1/4"
                :functionality="feature"
              />
            </div>
          </ProjectSpecialSection>

          <ProjectSpecialSection
            v-if="item.project.testimonial"
            id="testimonial"
            :title="$t('projects.testimonial')"
          >
            <TestimonialCard
              class="mx-auto w-full md:w-2/3"
              :testimonial="item.project.testimonial"
            />
          </ProjectSpecialSection>

          <section>
            <div class="mx-auto flex w-full justify-between md:w-2/3">
              <NuxtLink
                v-if="item.previousSlug"
                :to="
                  localePath(
                    `/projects/${route.params.industry}/${item.previousSlug}`
                  )
                "
                class="group mr-1 mt-4 flex items-center gap-x-2 py-3 font-medium text-gray-700 md:justify-start"
              >
                <Icon
                  name="lucide:move-left"
                  size="18"
                  class="transition group-hover:-translate-x-1"
                />
                {{ $t("projects.previous") }}
              </NuxtLink>

              <NuxtLink
                v-if="item.nextSlug"
                :to="
                  localePath(
                    `/projects/${route.params.industry}/${item.nextSlug}`
                  )
                "
                class="group ml-auto mt-4 flex items-center gap-x-2 py-3 font-medium text-gray-700 md:justify-end"
              >
                {{ $t("projects.next") }}
                <Icon
                  name="lucide:move-right"
                  size="18"
                  class="transition group-hover:translate-x-1"
                />
              </NuxtLink>
            </div>
          </section>
        </div>
      </Container>
      <Cta
        :href="localePath('/contact')"
        :label="$t('home.contact.ctaLabel')"
        class="!pb-32"
      />
    </div>
  </Loader>
</template>
